import React, { Fragment, useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button, Col, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row,
} from "reactstrap";
import { Settings } from "react-feather";
import { FaDownload } from "react-icons/fa";
import axios from "axios";
import moment from "moment";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../../../../AbstractElements";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import useRbac from "../../../../Hooks/useRbac";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "NOT_STARTED", label: "Not Started" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "REWARD_UNLOCKED", label: "Reward Unlocked" },
  { value: "REWARD_CLAIMED", label: "Reward Claimed" },
  { value: "EXPIRED", label: "Expired" },
];

const STATUS_COLORS = {
  IN_PROGRESS: "#0d6efd",
  REWARD_UNLOCKED: "#d3178a",
  REWARD_CLAIMED: "#008800",
  EXPIRED: "#999999",
};

const CustomerRewardsTable = () => {
  const { can } = useRbac();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((p) => !p);
  const [isExporting, setIsExporting] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem("token"));

  // ---------- API ----------
  const fetchCustomerRewards = async (page = 1, limit = perPage, status = statusFilter) => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseURL}/api/admin/customer-rewards`, {
        params: { page, limit, ...(status ? { status } : {}) },
        headers: { Authorization: `${getToken()}` },
      });
      setData(res.data.data || []);
      setTotalRows(res.data.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const claimReward = async (windowId) => {
    const result = await Swal.fire({
      title: "Mark this reward as claimed?",
      text: "Confirm the gift has been fulfilled/handed over to the customer.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d3178a",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, mark as claimed",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.patch(
        `${baseURL}/api/admin/customer-rewards/${windowId}/claim`,
        {},
        { headers: { Authorization: `${getToken()}` } }
      );
      Swal.fire({ title: "Reward Marked as Claimed!", icon: "success", confirmButtonColor: "#d3178a" });
      fetchCustomerRewards(1);
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Failed to mark as claimed",
        text: err?.response?.data?.message || "",
        icon: "error",
        confirmButtonColor: "#d3178a",
      });
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchCustomerRewards(1, perPage, value);
  };

  // Exports every matching row (not just the current page) as CSV.
  // Respects statusFilter — "All" exports everything, a specific status exports only that status.
  const exportRewards = async () => {
    setIsExporting(true);
    try {
      const res = await axios.get(`${baseURL}/api/admin/customer-rewards`, {
        params: {
          page: 1,
          limit: 999999,
          ...(statusFilter ? { status: statusFilter } : {}),
        },
        headers: { Authorization: `${getToken()}` },
      });

      const rewards = res?.data?.data || [];
      if (!rewards.length) {
        Swal.fire({ title: "No data to export", icon: "info", confirmButtonColor: "#d3178a" });
        return;
      }

      const csvData = rewards.map((row) => ({
        Customer: `${row.userId?.first_name || ""} ${row.userId?.last_name || ""}`.trim(),
        Email: row.userId?.email || "",
        Phone: row.userId?.phone || "",
        Progress: `${row.eligibleOrders?.length || 0} of ${row.requiredOrders}`,
        Gift_Variant: row.giftVariant?.variantName || "Not set",
        Window_Ends: row.rewardEndDate ? moment(row.rewardEndDate).format("DD-MM-YYYY") : "",
        Status: row.status?.replace(/_/g, " ") || "",
        Started: row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "",
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const statusLabel = STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label || "All";
      saveAs(blob, `customer_rewards_${statusLabel.replace(/\s+/g, "_")}_${moment().format("YYYYMMDD_HHmm")}.csv`);
    } catch (err) {
      console.log(err);
      Swal.fire({ title: "Failed to export customer rewards", icon: "error", confirmButtonColor: "#d3178a" });
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- pagination ----------
  const handlePageChange = (page) => fetchCustomerRewards(page, perPage);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchCustomerRewards(page, newPerPage);
  };

  useEffect(() => {
    fetchCustomerRewards(1);
    // eslint-disable-next-line
  }, []);

  const handleRowSelected = useCallback(() => {}, []);

  // ---------- columns ----------
  const columns = [
    {
      name: "Customer",
      selector: (row) => row.userId?.first_name,
      center: true,
      cell: (row) => (
        <div style={{ textAlign: "left" }}>
          <div>{row.userId?.first_name} {row.userId?.last_name}</div>
          <div style={{ fontSize: "0.8em", color: "#666" }}>{row.userId?.email || row.userId?.phone}</div>
        </div>
      ),
    },
    {
      name: "Progress",
      center: true,
      cell: (row) => (
        <span>{row.eligibleOrders?.length || 0} of {row.requiredOrders}</span>
      ),
    },
    {
      name: "Gift Variant",
      center: true,
      cell: (row) => row.giftVariant?.variantName || "Not set",
    },
    {
      name: "Window Ends",
      center: true,
      cell: (row) => moment(row.rewardEndDate).format("DD MMM YYYY"),
    },
    {
      name: "Status",
      center: true,
      cell: (row) => (
        <span
          style={{
            fontSize: "13px",
            fontWeight: 500,
            color: STATUS_COLORS[row.status] || "#333",
          }}
        >
          {row.status?.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      name: "Started",
      center: true,
      cell: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
    },
    {
      name: "Actions",
      center: true,
      omit: !can('reward.fulfill'),
      cell: (row) =>
        row.status === "REWARD_UNLOCKED" ? (
          <Button
            size="sm"
            className="btn btn-primary"
            onClick={() => claimReward(row._id)}
          >
            Mark as Claimed
          </Button>
        ) : (
          <span style={{ color: "#bbb", fontSize: "12px" }}>—</span>
        ),
    },
  ];

  return (
    <Fragment>
      <Row xxl={12} className="pb-4">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-bolder">Customer Rewards</h5>
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <Button
              className="btn btn-primary d-flex align-items-center"
              onClick={() => navigate("/reward-settings")}
            >
              <Settings size={16} className="me-2" />
              Reward Settings
            </Button>
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
              <DropdownToggle caret style={{ minWidth: "210px", whiteSpace: "nowrap" }}>
                Status: {STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label || "All"}
              </DropdownToggle>
              <DropdownMenu>
                {STATUS_OPTIONS.map((opt) => (
                  <DropdownItem key={opt.value} onClick={() => handleStatusFilterChange(opt.value)}>
                    {opt.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <Button
              className="btn btn-primary d-flex align-items-center"
              onClick={exportRewards}
              disabled={isExporting}
              style={{ gap: "8px", opacity: isExporting ? 0.7 : 1 }}
              title={statusFilter ? `Export "${STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label}" rewards to CSV` : "Export all customer rewards to CSV"}
            >
              <FaDownload size={14} />
              {isExporting ? "Exporting…" : "Export"}
            </Button>
          </div>
        </div>
      </Row>

      {data.length !== 0 ? (
        <DataTable
          data={data}
          columns={columns}
          pagination
          paginationServer
          progressPending={loading}
          progressComponent={<Loader />}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          onSelectedRowsChange={handleRowSelected}
        />
      ) : loading ? (
        <Col xxl="12" className="flex justify-center items-center">
          <div className="loader-box">
            <Spinner />
          </div>
        </Col>
      ) : (
        <Container className="text-center py-5 text-muted">
          No customer reward activity yet.
        </Container>
      )}
    </Fragment>
  );
};

export default CustomerRewardsTable;